import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, logout } from '../api';

const STATUS_OPTIONS = [
  'all', 'New', 'Elizabeth Called', 'Hot Lead', 'Gideon Called',
  'Closed Won', 'Closed Lost', 'Not Interested'
];

const STATUS_TINTS = {
  'New': '#6aa1ff',
  'Elizabeth Called': '#c8a064',
  'Hot Lead': '#ff7e3f',
  'Gideon Called': '#b88be0',
  'Closed Won': '#4ad17a',
  'Closed Lost': '#7a7d85',
  'Not Interested': '#7a7d85'
};

export default function LeadList() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, hot: 0, byStatus: [] });
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        api.listLeads({ status: statusFilter === 'all' ? '' : statusFilter, q: query }),
        api.stats()
      ]);
      setLeads(list);
      setStats(s);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { api.me().then(setCurrentUser).catch(() => {}); }, []);

  async function exportCSV() {
    try { await api.exportCSV(); }
    catch (err) {
      if (err.message === 'forbidden') alert('Export is restricted to admins.');
      else alert('Export failed.');
    }
  }

  return (
    <div className="crm-shell">
      <header className="crm-header">
        <div className="crm-brand">
          <span className="brand-mark tiny"><img src="/brand.jpg" alt="" onError={e => { e.target.style.display = 'none'; e.target.parentElement.classList.add('text-fallback'); e.target.parentElement.textContent = 'LHAI'; }} /></span>
          <div>
            <h1>Layah Heilpern AI Automation</h1>
            <p>Sales pipeline</p>
          </div>
        </div>
        <button className="btn-ghost" onClick={logout}>Sign out</button>
      </header>

      <div className="stats-row">
        <div className="stat"><span className="stat-num">{stats.total}</span><span className="stat-lbl">Total leads</span></div>
        <div className="stat"><span className="stat-num">{stats.hot}</span><span className="stat-lbl">Hot leads</span></div>
        {stats.byStatus.filter(s => ['New','Elizabeth Called','Hot Lead','Gideon Called'].includes(s.status)).map(s => (
          <div key={s.status} className="stat"><span className="stat-num">{s.count}</span><span className="stat-lbl">{s.status}</span></div>
        ))}
      </div>

      <div className="crm-toolbar">
        <input
          className="text-input"
          placeholder="Search by name, email or telegram..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
        />
        <select className="text-input select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>)}
        </select>
        <button className="btn-ghost" onClick={load}>Refresh</button>
      </div>

      {(() => {
        if (statusFilter !== 'all') return null;
        const hot = leads.filter(l => l.hot_lead && !['Gideon Called','Closed Won','Closed Lost','Not Interested'].includes(l.status));
        if (!hot.length) return null;
        return (
          <div className="hot-banner">
            <div className="hot-banner-title">
              <span>🔥 Hot leads, ready for Gideon</span>
              <span className="count">{hot.length}</span>
            </div>
            <div className="hot-banner-list">
              {hot.map(l => (
                <Link key={l.id} to={`/crm/lead/${l.id}`} className="hot-banner-row">
                  <div><strong>{l.name}</strong></div>
                  <div className="muted">{l.telegram_handle}</div>
                  <div className="muted">{l.country}</div>
                  <div className="muted">{l.savings_range}</div>
                  <div className="arrow">→</div>
                </Link>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="lead-table">
        <div className="lead-row head">
          <div>Name</div>
          <div>Telegram</div>
          <div>Country</div>
          <div>Savings</div>
          <div>Status</div>
          <div>Submitted</div>
        </div>
        {loading && <div className="empty">Loading...</div>}
        {(() => {
          // Hot leads belong in the hot section / Hot Lead filter only.
          // For every other filter ("all", "New", "Elizabeth Called", etc), exclude pending hot leads.
          const visible = statusFilter === 'Hot Lead'
            ? leads
            : leads.filter(l => !(l.hot_lead && !['Gideon Called','Closed Won','Closed Lost','Not Interested'].includes(l.status)));
          if (!loading && visible.length === 0) return <div className="empty">No leads yet.</div>;
          return visible.map(l => (
          <Link key={l.id} to={`/crm/lead/${l.id}`} className="lead-row">
            <div>
              {l.hot_lead && <span className="hot-dot" title="Hot lead">🔥</span>}
              {l.name}
            </div>
            <div className="muted">{l.telegram_handle}</div>
            <div className="muted">{l.country}</div>
            <div className="muted">{l.savings_range}</div>
            <div>
              <span className="status-pill" style={{ '--tint': STATUS_TINTS[l.status] || '#888' }}>
                {l.status}
              </span>
            </div>
            <div className="muted small">{new Date(l.created_at).toLocaleDateString()}</div>
          </Link>
          ));
        })()}
      </div>

      {currentUser?.role === 'admin' && (
        <button className="export-link" onClick={exportCSV} disabled={!leads.length}>
          Export CSV
        </button>
      )}
    </div>
  );
}
